import { CompanyType } from '@finverity/graphql';
import {
  AuthService,
  IdentityAccessValueFactory,
  PolicyService,
  PolicyType,
  provideIdentityAccessPermissions,
} from '@finverity/identity-access';
import { INSTRUMENT_UPLOAD_PAGE_PERMISSIONS } from '@finverity/platform-app/modules/instrument-upload/identity-access';

import { NavigationMenuPermissions } from './navigation-menu-permissions';

const NAVIGATION_MENU_IDENTITY_ACCESS_FACTORY: IdentityAccessValueFactory<NavigationMenuPermissions> = (
  authService: AuthService,
  policyService: PolicyService
) => {
  const isPortalAdmin = authService.authUser()?.company_type === CompanyType.PortalAdmin;

  return {
    [NavigationMenuPermissions.ViewDashboard]: policyService.hasPolicies(PolicyType.VIEW_DASHBOARD),
    [NavigationMenuPermissions.ViewCompanies]: policyService.hasPermissions([
      'CREATE_AND_EDIT_COMPANIES',
      'VIEW_COMPANIES',
    ]),
    [NavigationMenuPermissions.ViewSelfOnboarding]: policyService.hasPermissions([
      'VIEW_SELF_ONBOARDING_ADMIN',
      'MANAGE_SELF_ONBOARDING_ADMIN',
    ]),

    [NavigationMenuPermissions.ProductsManagement]: policyService.hasPermissions([
      'CREATE_AND_EDIT_PRODUCT_GROUPS',
      'VIEW_PRODUCT_GROUPS',
      'DELETE_PRODUCT_GROUPS',
      'CREATE_AND_EDIT_PRODUCT_STRUCTURES',
      'VIEW_PRODUCT_STRUCTURES',
      'DELETE_PRODUCT_STRUCTURES',
    ]),

    [NavigationMenuPermissions.FxAndFloatingRates]: policyService.hasPermissions([
      'CREATE_AND_EDIT_FLOTING_RATES',
      'VIEW_FLOATING_RATES',
      'DELETE_FLOATING_RATES',
      'CREATE_AND_EDIT_FX_RATES',
      'DELETE_FX_RATES',
      'VIEW_FX_RATES',
    ]),

    [NavigationMenuPermissions.ViewProductGroups]: policyService.hasPermissions([
      'CREATE_AND_EDIT_PRODUCT_GROUPS',
      'VIEW_PRODUCT_GROUPS',
      'DELETE_PRODUCT_GROUPS',
    ]),
    [NavigationMenuPermissions.ViewProductStructures]: policyService.hasPermissions([
      'CREATE_AND_EDIT_PRODUCT_STRUCTURES',
      'VIEW_PRODUCT_STRUCTURES',
      'DELETE_PRODUCT_STRUCTURES',
    ]),
    [NavigationMenuPermissions.ViewFloatingRates]: policyService.hasPermissions([
      'CREATE_AND_EDIT_FLOTING_RATES',
      'VIEW_FLOATING_RATES',
      'DELETE_FLOATING_RATES',
    ]),
    [NavigationMenuPermissions.ViewFXRates]: policyService.hasPermissions([
      'CREATE_AND_EDIT_FX_RATES',
      'VIEW_FX_RATES',
      'DELETE_FX_RATES',
    ]),

    [NavigationMenuPermissions.ViewProgrammesAndDeals]: policyService.hasPermissions([
      'VIEW_PROGRAMMES',
      'CREATE_AND_EDIT_PROGRAMMES',
      'DELETE_PROGRAMMES',
      'VIEW_DEALS',
      'CREATE_AND_EDIT_DEALS',
      'MANAGE_INSTRUMENT_APPROVAL_WORKFLOW_SETTINGS_ADMIN',
    ]),
    [NavigationMenuPermissions.ViewProgrammes]: policyService.hasPermissions([
      'VIEW_PROGRAMMES',
      'CREATE_AND_EDIT_PROGRAMMES',
      'DELETE_PROGRAMMES',
    ]),
    [NavigationMenuPermissions.ViewDeals]: policyService.hasPermissions([
      'VIEW_DEALS',
      'CREATE_AND_EDIT_DEALS',
      'MANAGE_INSTRUMENT_APPROVAL_WORKFLOW_SETTINGS_ADMIN',
    ]),
    [NavigationMenuPermissions.ViewBankHolidayRules]: policyService.hasPolicies(PolicyType.VIEW_BANK_HOLIDAY_RULES),
    [NavigationMenuPermissions.ViewCreditNoteRules]: policyService.hasPolicies(PolicyType.VIEW_CREDIT_NOTE_RULES),
    [NavigationMenuPermissions.ViewInstrumentUpload]: policyService.hasPermissions(INSTRUMENT_UPLOAD_PAGE_PERMISSIONS),
    // TODO: FDS-3286 - replace the policy check with a dedicated permission if BE adds one
    [NavigationMenuPermissions.ViewAllocatedCreditNotes]: policyService.hasPolicies(PolicyType.VIEW_INSTRUMENT_DETAILS),
    [NavigationMenuPermissions.ViewUserManagement]: policyService.hasPermissions([
      'MANAGE_MY_COMPANY_USERS_ADMIN',
      'MANAGE_MY_COMPANY_USERS_CUSTOMER',
      'MANAGE_MY_COMPANY_USERS_FUNDER',
    ]),
    [NavigationMenuPermissions.ViewServiceAccountManagement]: policyService.hasPermissions([
      'MANAGE_SERVICE_ACCOUNTS',
      'MANAGE_SERVICE_ACCOUNTS_CUSTOMER',
      'MANAGE_SERVICE_ACCOUNTS_FUNDER',
    ]),
    [NavigationMenuPermissions.ViewAuditTrail]: policyService.hasPolicies(PolicyType.VIEW_AUDIT_TRAIL),
    [NavigationMenuPermissions.ViewScheduledJobs]: policyService.hasPolicies(PolicyType.VIEW_SCHEDULED_JOBS),
    [NavigationMenuPermissions.ViewCompanyNotifications]:
      policyService.hasPolicies(PolicyType.VIEW_INTERNAL_USER_ROLES) &&
      policyService.hasPolicies(PolicyType.VIEW_ROLE_NOTIFICATION_PROPERTIES),
    [NavigationMenuPermissions.ViewCompanySsoSettings]: policyService.hasPolicies(
      PolicyType.VIEW_AUTHENTICATION_METHODS
    ),
    [NavigationMenuPermissions.ViewCompanySecuritySettings]: policyService.hasPolicies(
      PolicyType.CONFIGURE_MANDATORY_2FA_ON_LOGIN
    ),
    [NavigationMenuPermissions.CustomerViewCompanyDetails]: policyService.hasPermissions([
      'MANAGE_MY_COMPANY_CUSTOMER',
    ]),

    [NavigationMenuPermissions.InstrumentApproval]:
      policyService.hasPolicies(PolicyType.VIEW_INSTRUMENT_DETAILS) &&
      policyService.hasPolicies(PolicyType.VIEW_DOCUMENTS),
    [NavigationMenuPermissions.ApproveRejectInstrumentsPayerApprovalMaker]:
      policyService.hasPolicies(PolicyType.CONFIRM_INSTRUMENTS_PAYER_APPROVAL_MAKER) &&
      policyService.hasPolicies(PolicyType.REJECT_INSTRUMENTS_PAYER_APPROVAL_MAKER),
    [NavigationMenuPermissions.ApproveRejectInstrumentsPayerApprovalChecker]:
      policyService.hasPolicies(PolicyType.CONFIRM_INSTRUMENTS_PAYER_APPROVAL_CHECKER) &&
      policyService.hasPolicies(PolicyType.REJECT_INSTRUMENTS_PAYER_APPROVAL_CHECKER),
    [NavigationMenuPermissions.ManageRejectedInstrumentsPayerApproval]:
      policyService.hasPolicies(PolicyType.EDIT_INSTRUMENTS) &&
      policyService.hasPolicies(PolicyType.UNDO_REJECTION_PAYER_APPROVAL),
    [NavigationMenuPermissions.MarkAsPaidWithoutFinancing]: policyService.hasPolicies(
      PolicyType.MARK_INSTRUMENTS_AS_PAID
    ),

    [NavigationMenuPermissions.ViewRepayments]: policyService.hasPermissions([
      'VIEW_REPAYMENTS_FUNDER',
      'VIEW_REPAYMENTS_CUSTOMER',
      'VIEW_REPAYMENTS_ADMIN',
    ]),
    // VIEW_REPAYMENTS comes with every base permission, but the page lists instruments
    [NavigationMenuPermissions.ViewOutstandingInstruments]:
      policyService.hasPolicies(PolicyType.VIEW_REPAYMENTS) &&
      policyService.hasPolicies(PolicyType.VIEW_INSTRUMENT_DETAILS),
    [NavigationMenuPermissions.ViewNplStatusApprovals]:
      policyService.hasPolicies(PolicyType.APPROVE_INSTRUMENT_NPL_STATUS_CHANGE) &&
      policyService.hasPermissions([
        'MANAGE_INSTRUMENT_NPL_STATUS_CHECKER',
        'MANAGE_INSTRUMENT_NPL_STATUS_CHECKER_ADMIN',
      ]),
    [NavigationMenuPermissions.FinanceRequest]:
      policyService.hasPolicies(PolicyType.VIEW_INSTRUMENT_DETAILS) &&
      policyService.hasPolicies(PolicyType.VIEW_DOCUMENTS),
    [NavigationMenuPermissions.ApproveRejectInstrumentsSellerFinanceRequestMaker]:
      policyService.hasPolicies(PolicyType.CONFIRM_INSTRUMENTS_SELLER_FINANCE_REQUEST_MAKER) &&
      policyService.hasPolicies(PolicyType.REJECT_INSTRUMENTS_SELLER_FINANCE_REQUEST_MAKER),
    [NavigationMenuPermissions.ApproveRejectInstrumentsSellerFinanceRequestChecker]:
      policyService.hasPolicies(PolicyType.CONFIRM_INSTRUMENTS_SELLER_FINANCE_REQUEST_CHECKER) &&
      policyService.hasPolicies(PolicyType.REJECT_INSTRUMENTS_SELLER_FINANCE_REQUEST_CHECKER),
    [NavigationMenuPermissions.ManageRejectedInstrumentsSellerFinanceRequest]:
      policyService.hasPolicies(PolicyType.EDIT_INSTRUMENTS) &&
      policyService.hasPolicies(PolicyType.UNDO_REJECTION_SELLER_FINANCE_REQUEST),
    [NavigationMenuPermissions.ApproveRejectInstrumentsPayerFinanceRequestMaker]:
      policyService.hasPolicies(PolicyType.CONFIRM_INSTRUMENTS_PAYER_FINANCE_REQUEST_MAKER) &&
      policyService.hasPolicies(PolicyType.REJECT_INSTRUMENTS_PAYER_FINANCE_REQUEST_MAKER),
    [NavigationMenuPermissions.ApproveRejectInstrumentsPayerFinanceRequestChecker]:
      policyService.hasPolicies(PolicyType.CONFIRM_INSTRUMENTS_PAYER_FINANCE_REQUEST_CHECKER) &&
      policyService.hasPolicies(PolicyType.REJECT_INSTRUMENTS_PAYER_FINANCE_REQUEST_CHECKER),
    [NavigationMenuPermissions.ManageRejectedInstrumentsPayerFinanceRequest]:
      policyService.hasPolicies(PolicyType.EDIT_INSTRUMENTS) &&
      policyService.hasPolicies(PolicyType.UNDO_REJECTION_PAYER_FINANCE_REQUEST),

    [NavigationMenuPermissions.ReadyForFunding]: policyService.hasPermissions([
      'FUND_INSTRUMENTS_ADMIN',
      'FUND_INSTRUMENTS_FUNDER',
    ]),

    [NavigationMenuPermissions.ViewIncomingPayments]: policyService.hasPolicies(PolicyType.VIEW_INCOMING_PAYMENTS),

    [NavigationMenuPermissions.ViewDisbursementsSectionAdmin]: policyService.hasPermissions([
      'CONFIRM_AND_CANCEL_DISBURSEMENTS_ADMIN',
      'VIEW_DISBURSEMENT_FILES_ADMIN',
    ]),
    [NavigationMenuPermissions.ViewDisbursementsSectionFunder]: policyService.hasPermissions([
      'CONFIRM_AND_CANCEL_DISBURSEMENTS_FUNDER',
      'VIEW_DISBURSEMENT_FILES_FUNDER',
    ]),

    [NavigationMenuPermissions.InstrumentDisbursementsAdmin]: policyService.hasPermissions([
      'CONFIRM_AND_CANCEL_DISBURSEMENTS_ADMIN',
    ]),
    [NavigationMenuPermissions.InstrumentDisbursementsFunder]: policyService.hasPermissions([
      'CONFIRM_AND_CANCEL_DISBURSEMENTS_FUNDER',
    ]),
    [NavigationMenuPermissions.ViewDisbursementFilesAdmin]: policyService.hasPermissions(
      'VIEW_DISBURSEMENT_FILES_ADMIN'
    ),
    [NavigationMenuPermissions.ViewDisbursementFilesFunder]: policyService.hasPermissions(
      'VIEW_DISBURSEMENT_FILES_FUNDER'
    ),

    [NavigationMenuPermissions.FinanceApprovalFunder]:
      policyService.hasPolicies(PolicyType.VIEW_INSTRUMENT_DETAILS) &&
      policyService.hasPolicies(PolicyType.VIEW_DOCUMENTS),
    [NavigationMenuPermissions.ApproveRejectInstrumentsFunderApprovalMaker]:
      policyService.hasPolicies(PolicyType.CONFIRM_INSTRUMENTS_FUNDER_APPROVAL_MAKER) &&
      policyService.hasPolicies(PolicyType.REJECT_INSTRUMENTS_FUNDER_APPROVAL_MAKER),
    [NavigationMenuPermissions.ApproveRejectInstrumentsFunderApprovalChecker]:
      policyService.hasPolicies(PolicyType.CONFIRM_INSTRUMENTS_FUNDER_APPROVAL_CHECKER) &&
      policyService.hasPolicies(PolicyType.REJECT_INSTRUMENTS_FUNDER_APPROVAL_CHECKER),
    [NavigationMenuPermissions.ManageRejectedInstrumentsFunderApproval]:
      policyService.hasPolicies(PolicyType.EDIT_INSTRUMENTS) &&
      policyService.hasPolicies(PolicyType.UNDO_REJECTION_FUNDER_APPROVAL),
    [NavigationMenuPermissions.ManageFunderRejectedInstrumentsCustomer]:
      policyService.hasPolicies(PolicyType.EDIT_INSTRUMENTS) &&
      policyService.hasPolicies(PolicyType.UNDO_REJECTION_FUNDER_APPROVAL),

    [NavigationMenuPermissions.ViewFunderApprovalPagesAdmin]: policyService.hasPermissions([
      'APPROVE_REJECT_INSTRUMENTS_FUNDER_APPROVAL_MAKER_ADMIN',
      'APPROVE_REJECT_INSTRUMENTS_FUNDER_APPROVAL_CHECKER_ADMIN',
      'MANAGE_REJECTED_INSTRUMENTS_FUNDER_APPROVAL_ADMIN',
    ]),
    [NavigationMenuPermissions.ApproveRejectInstrumentsFunderApprovalMakerAdmin]: policyService.hasPermissions([
      'APPROVE_REJECT_INSTRUMENTS_FUNDER_APPROVAL_MAKER_ADMIN',
    ]),
    [NavigationMenuPermissions.ApproveRejectInstrumentsFunderApprovalCheckerAdmin]: policyService.hasPermissions([
      'APPROVE_REJECT_INSTRUMENTS_FUNDER_APPROVAL_CHECKER_ADMIN',
    ]),
    [NavigationMenuPermissions.ManageRejectedInstrumentsFunderApprovalAdmin]: policyService.hasPermissions([
      'MANAGE_REJECTED_INSTRUMENTS_FUNDER_APPROVAL_ADMIN',
    ]),

    [NavigationMenuPermissions.ManageOnboardingFormAndRules]: policyService.hasPermissions([
      'MANAGE_ONBOARDING_FORM_AND_RULES',
    ]),

    [NavigationMenuPermissions.ViewWebhookManagement]: policyService.hasPolicies(PolicyType.VIEW_WEBHOOKS),
    [NavigationMenuPermissions.ViewSystemHealthNotifications]: policyService.hasPolicies(PolicyType.VIEW_WEBHOOKS),
    [NavigationMenuPermissions.ViewNotificationTemplates]: policyService.hasPolicies(
      PolicyType.LIST_NOTIFICATION_TEMPLATES
    ),

    // TODO: FDS-4267 Replace PortalAdmin stopgap with real BE policy once contract templates policies land.
    [NavigationMenuPermissions.ViewContractTemplates]: isPortalAdmin,

    // TODO: FDS-4574 Replace PortalAdmin stopgap with PolicyType.VIEW_CUSTOM_FIELDS once the BE policy lands.
    [NavigationMenuPermissions.ViewCustomFields]: isPortalAdmin,
    [NavigationMenuPermissions.ViewTransactionLimits]: policyService.hasPolicies(PolicyType.VIEW_TRANSACTION_LIMITS),
  };
};

export const NAVIGATION_MENU_IDENTITY_ACCESS_PROVIDER = provideIdentityAccessPermissions(
  NAVIGATION_MENU_IDENTITY_ACCESS_FACTORY
);
